/*
 * BuildDocs пользовательский интерфейс version 0.1.0
 *
 * Copyright © 2022 ООО АДАПТ info@acfs.spb.ru
 * You may use, distribute and modify this code under the
 * terms of the ООО АДАПТ license
 */

import {
  updateActs,
  updateSecondaryAct,
  UpdateSecondaryActType,
  UUID,
  ActForm,
} from '@builddocs/data-management'
import { updateAOSR } from '@builddocs/filestore-api'
import { ToastType } from '@builddocs/system'
import * as Sentry from '@sentry/react'

import {
  AOSRJsonType,
  ListsFieldsType,
  SecondaryActJsonType,
} from '../Types/dataJSON'

import { fieldForAddress } from './getEditField'

export const updateAct = (
  data: {
    contractId: UUID
    actId: UUID
    dataJSON: AOSRJsonType | SecondaryActJsonType
    additionalFields: ListsFieldsType
    showCertAOSR: boolean
    shouldGenerateRegistry: boolean
    forceIncludeOrganization: string[] | undefined
    versionRemarkExon: number | null
    assigneesForActRegistry: Record<string, UUID[]> | null
    shouldIncludeSupportingsInProjectDocuments: boolean
    shouldShowMaterials: boolean
    shouldShowLaboratories: boolean
    shouldShowExecutiveSchemes: boolean
  },
  opts: {
    toast: ToastType
  },
  oldActData?: AOSRJsonType | SecondaryActJsonType,
) => {
  const { toast } = opts

  const materialFields = data.additionalFields.selectMaterials
    ? data.additionalFields.selectMaterials.reduce(
        (acc, key) => ({
          ...acc,
          [key]: {
            selectedMaterials: data.dataJSON[key]?.selectedMaterials ?? [],
          },
        }),
        {},
      )
    : {}

  const workFields = data.additionalFields.selectedWorks
    ? data.additionalFields.selectedWorks.reduce(
        (acc, key) => ({
          ...acc,
          [key]: {
            selectedWorks: data.dataJSON[key]?.selectedWorks ?? [],
          },
        }),
        {},
      )
    : {}

  const documentFields = data.additionalFields.selectDocuments
    ? data.additionalFields.selectDocuments.reduce<Record<string, string[]>>(
        (acc, field) => {
          const key = Object.keys(field)[0]

          const flatDocsIds = field[key].reduce<string[]>((acc, type) => {
            if (
              !(
                data.dataJSON[key]?.manualField &&
                fieldForAddress[type] in data.dataJSON[key].manualField &&
                Array.isArray(
                  data.dataJSON[key].manualField[fieldForAddress[type]],
                )
              )
            ) {
              return acc
            }

            const docsIds = data.dataJSON[key].manualField[
              fieldForAddress[type]
            ].map(doc => doc.id)

            return [...acc, ...docsIds]
          }, [])

          return {
            ...acc,
            [key]: flatDocsIds,
          }
        },
        {},
      )
    : {}

  const textFields = data.additionalFields.textFields
    ? data.additionalFields.textFields?.reduce(
        (acc: Record<string, any>, key: string) => ({
          ...acc,
          [key]: data.dataJSON[key]?.value,
        }),
        {},
      )
    : {}

  const manualAssignees = data.additionalFields.assigneesFields
    ? data.additionalFields.assigneesFields?.reduce(
        (acc: UpdateSecondaryActType['manualAssignees'], key: string) => ({
          ...acc,
          [key]:
            data.dataJSON[key]?.manualAssignees &&
            Object.keys(data.dataJSON[key].manualAssignees),
        }),
        {},
      )
    : {}

  const overwrittenAssignees = data.additionalFields.assigneesFields.reduce(
    (acc: Record<string, UUID[]>, key: string) => {
      const overwrittenAssignees = data.dataJSON[key]?.overwrittenAssignees

      if (!overwrittenAssignees) {
        return acc
      }

      return {
        ...acc,
        [key]: Object.keys(overwrittenAssignees),
      }
    },
    {} as Record<string, UUID[]>,
  )

  const manualOrganizations = data.additionalFields.organizationsFields
    ? data.additionalFields.organizationsFields?.reduce(
        (acc: UpdateSecondaryActType['manualOrganizations'], key: string) => ({
          ...acc,
          [key]: data.dataJSON[key]?.manualOrganization
            ? {
                inn: data.dataJSON[key].manualOrganization?.inn,
                name: data.dataJSON[key].manualOrganization?.name,
              }
            : null,
        }),
        {},
      )
    : {}

  return data.dataJSON.act_type === ActForm.AOSR
    ? updateAOSR(data.dataJSON['acceptance_object'].completedWorks)
        .then(response => {
          if (response.status === 200) {
            toast.info('Изменения в документах сохранены')

            let isDateChanged = false

            if (
              data.dataJSON.act_type === ActForm.AOSR &&
              oldActData?.act_type === ActForm.AOSR
            ) {
              const { works } =
                data.dataJSON['acceptance_object'].completedWorks

              oldActData?.acceptance_object.completedWorks.works.forEach(w => {
                isDateChanged =
                  isDateChanged || !works.find(pw => pw.workDate === w.workDate)
              })
            }

            data.dataJSON.act_type === ActForm.AOSR &&
              updateActs({
                isDateChangedWithoutIntegration: isDateChanged,
                contractId: data.contractId,
                actId: data.actId,
                actType: ActForm.AOSR,
                nextWork: data.dataJSON['next_works']?.value,
                executiveSchemes:
                  data.dataJSON['acceptance_object'].completedWorks.aosrDocs
                    .executiveSchemeIds,
                laboratoriesIds:
                  data.dataJSON['acceptance_object'].completedWorks.aosrDocs
                    .laboratoryIds,
                supportingDocsIds: data.dataJSON.applications.supportingIds,
                executiveDrawings:
                  data.dataJSON['acceptance_object'].completedWorks.aosrDocs
                    .executiveDrawingIds,
                numberCopies: data.dataJSON['number_copies']?.value,
                additionalInformation:
                  data.dataJSON['additional_information']?.value,
                showCertAOSR: data.showCertAOSR,
                shouldGenerateRegistry: data.shouldGenerateRegistry,
                forceIncludeOrganization: data.forceIncludeOrganization ?? [],
                versionRemarkExon: data.versionRemarkExon,
                assigneesForActRegistry: data.assigneesForActRegistry,
                shouldIncludeSupportingsInProjectDocuments:
                  data.shouldIncludeSupportingsInProjectDocuments,
                supportingDocsForWorksIds:
                  data.dataJSON.documents_for_work.supportingDocsForWorksIds,
                overwrittenAssignees,
                useOverwrittenAssignee: true,
              })
                .then(response => {
                  if (response.status === 200) {
                    toast.info('Обновление акта')
                  }
                })
                .catch(e => {
                  Sentry.captureException(e, {
                    tags: {
                      section: 'interactiveAct',
                      action: 'regeneration AOSR',
                    },
                  })
                  if (e.status === 400) {
                    toast.warning(
                      e?.response?.data?.message ??
                        'Не удалось обновить акт. В журнале мало записей. Возвращайтесь сюда позже.',
                    )
                  } else {
                    toast.error(
                      e?.response?.data?.message ?? 'Не удалось сгенерировать',
                    )
                  }
                })
          }
        })
        .catch(e => {
          Sentry.captureException(e, {
            tags: {
              section: 'interactiveAct',
              action: 'updating fields original source',
            },
          })
          if (e.status === 400) {
            toast.warning(
              e?.response?.data?.message ??
                'Не удалось внести изменения в первоисточник. 400',
            )
          } else {
            toast.error(
              e?.response?.data?.message ??
                'Не удалось внести изменения в первоисточник.',
            )
          }
        })
    : updateSecondaryAct({
        contractId: data.contractId,
        actId: data.actId,
        actDate: data.dataJSON.act_date.value,
        versionRemarkExon: data.versionRemarkExon,
        shouldGenerateRegistry: data.shouldGenerateRegistry,
        assigneesForActRegistry: data.assigneesForActRegistry,
        manualFields: {
          conclusion_1: data.dataJSON.conclusion_1?.value,
          conclusion_2: data.dataJSON.conclusion_2?.value,
          conclusion_3: data.dataJSON.conclusion_3?.value,
          ...textFields,
          ...documentFields,
          ...materialFields,
          ...workFields,
        },
        manualAssignees,
        manualOrganizations,
        shouldShowMaterials: data.shouldShowMaterials,
        shouldShowLaboratories: data.shouldShowLaboratories,
        shouldShowExecutiveSchemes: data.shouldShowExecutiveSchemes,
        forceIncludeOrganization: data.forceIncludeOrganization ?? [],
      })
        .then(response => {
          if (response.status === 200) {
            toast.info('Обновляется. Акт отправлен на переформирование.')
          }
        })
        .catch(e => {
          Sentry.captureException(e, {
            tags: {
              section: 'interactiveAct',
              action: 'regeneration secondaryAct',
            },
          })
          if (e.status === 400) {
            toast.warning(
              e?.response?.data?.message ??
                'Не удалось обновить акт. В журнале мало записей. Возвращайтесь сюда позже.',
            )
          } else {
            toast.error(
              e?.response?.data?.message ?? 'Ошибка. Не удалось сгенерировать.',
            )
          }
        })
}
